import {
  AgencyUserState,
  MailboxAccountStatus,
  MailboxFolderRole as FolderRole,
  TriggerEmailAddressKind as AddressKind,
  TriggerEmailArrivalMode as Arrival,
  TriggerEmailPhraseField as PhraseField,
  TriggerEmailPrivacy as Privacy,
  TriggerEmailRecipientField as RecipientField,
  TriggerEmailRuleKind as Kind,
  TriggerEmailScopeMode as ScopeMode,
  TriggerEmailTextOperator as TextOperator,
  TriggerEmailTimeOperator as TimeOperator,
  TriggerEmailValueMode as ValueMode,
  TriggerEmailWeekday as Weekday,
  TriggerEventType,
  TriggerTargetType,
  type CreateTriggerDefinitionInput,
  type GetTriggerEmailBuilderOptionsQuery,
  type GetTriggerEmailFoldersQuery,
  type TriggerEmailAddressMatchInput,
  type TriggerEmailConfigFieldsFragment,
  type TriggerEmailConfigInput,
  type TriggerEmailRuleInput,
  type TriggerEmailTextMatchInput,
  type TriggerTargetInput,
  type VisibilityConfigInput
} from '~/api/graphql';

export const EMAIL_LIMITS = { depth: 8, nodes: 128, values: 32, prompts: 3, bytes: 2048 } as const;
/**
 * Weekdays in Monday-to-Sunday order. The API sends names, and the generated enum is
 * alphabetical, so use this list (not Object.values) for order.
 */
export const EMAIL_WEEKDAYS: readonly Weekday[] = [
  Weekday.Monday,
  Weekday.Tuesday,
  Weekday.Wednesday,
  Weekday.Thursday,
  Weekday.Friday,
  Weekday.Saturday,
  Weekday.Sunday
];
export const compareEmailWeekdays = (a: Weekday, b: Weekday) =>
  EMAIL_WEEKDAYS.indexOf(a) - EMAIL_WEEKDAYS.indexOf(b);
/** Mailbox folder roles a trigger may watch. Drafts stay a mailbox role but never match. */
export const EMAIL_FOLDER_ROLES = [
  FolderRole.Inbox,
  FolderRole.Sent,
  FolderRole.Archive,
  FolderRole.Junk,
  FolderRole.Trash
] as const;
export type EmailFolderRole = (typeof EMAIL_FOLDER_ROLES)[number];
export const isEmailFolderRole = (value: unknown): value is EmailFolderRole =>
  EMAIL_FOLDER_ROLES.some((role) => role === value);
export type EmailOptions = GetTriggerEmailBuilderOptionsQuery['triggerEmailBuilderOptions'];
export type EmailFolders = GetTriggerEmailFoldersQuery['triggerEmailFolders'];
export type EmailErrors = Record<string, string>;
export type EmailConfigDraft = TriggerEmailConfigInput & {
  schemaVersion: number;
  arrivals: Arrival;
  privacy: Privacy;
  timezone: string | null;
  rule: TriggerEmailRuleInput | null;
};

type RuleSource = Omit<TriggerEmailRuleInput, 'children'> & { children?: RuleSource[] };
const bytes = (text: string) => new TextEncoder().encode(text).length;
const validEnum = <T extends string>(value: unknown, choices: Record<string, T>): value is T =>
  Object.values(choices).includes(value as T);
export const isEmailGroup = (kind: Kind) => [Kind.And, Kind.Or, Kind.Not].includes(kind);
export const emptyEmailConfig = (): EmailConfigDraft => ({
  schemaVersion: 2,
  scope: { mode: ScopeMode.Selected, accounts: [] },
  arrivals: Arrival.New,
  privacy: Privacy.MatchedMailboxOwners,
  timezone: null,
  rule: null
});

export const newEmailRule = (
  kind: Kind,
  id: string = crypto.randomUUID()
): TriggerEmailRuleInput => {
  const base = { id, kind, children: [] };
  switch (kind) {
    case Kind.Phrase:
      return {
        ...base,
        phrase: {
          field: PhraseField.Subject,
          match: {
            operator: TextOperator.Contains,
            values: [],
            valueMode: ValueMode.Any,
            caseSensitive: false
          }
        }
      };
    case Kind.Sender:
      return {
        ...base,
        sender: {
          kind: AddressKind.Address,
          values: [],
          valueMode: ValueMode.Any,
          includeSubdomains: false
        }
      };
    case Kind.Recipient:
      return {
        ...base,
        recipient: {
          field: RecipientField.To,
          match: {
            kind: AddressKind.Address,
            values: [],
            valueMode: ValueMode.Any,
            includeSubdomains: false
          }
        }
      };
    case Kind.Time:
      return {
        ...base,
        // Every day by default: weekdays live behind the condition settings, and a
        // time window with no days can never be saved or match.
        time: {
          operator: TimeOperator.Between,
          minute: -1,
          endMinute: null,
          weekdays: [...EMAIL_WEEKDAYS]
        }
      };
    case Kind.Attachment:
      return {
        ...base,
        attachment: {
          minimumCount: 1,
          maximumCount: null,
          name: null,
          mimeTypes: [],
          extensions: [],
          minimumBytes: null,
          maximumBytes: null
        }
      };
    case Kind.Prompt:
      return { ...base, prompt: { prompt: '' } };
    default:
      return base;
  }
};

const cloneText = (match: TriggerEmailTextMatchInput): TriggerEmailTextMatchInput => ({
  operator: match.operator,
  values: [...match.values],
  valueMode: match.valueMode ?? ValueMode.Any,
  caseSensitive: match.caseSensitive ?? false
});
const cloneAddress = (match: TriggerEmailAddressMatchInput): TriggerEmailAddressMatchInput => ({
  kind: match.kind,
  values: [...match.values],
  valueMode: match.valueMode ?? ValueMode.Any,
  includeSubdomains: match.includeSubdomains ?? false
});

// Whitelist SDL fields, never JSON-round-trip the Apollo objects or delimiter-join values.
export const emailRuleInput = (rule: RuleSource, depth = 1): TriggerEmailRuleInput => {
  if (depth > EMAIL_LIMITS.depth) throw new Error('Condition tree exceeds the supported depth');
  return {
    id: rule.id,
    kind: rule.kind,
    children: (rule.children ?? []).map((child) => emailRuleInput(child, depth + 1)),
    ...(rule.phrase
      ? { phrase: { field: rule.phrase.field, match: cloneText(rule.phrase.match) } }
      : {}),
    ...(rule.sender ? { sender: cloneAddress(rule.sender) } : {}),
    ...(rule.recipient
      ? { recipient: { field: rule.recipient.field, match: cloneAddress(rule.recipient.match) } }
      : {}),
    ...(rule.time
      ? {
          time: {
            operator: rule.time.operator,
            minute: rule.time.minute,
            endMinute: rule.time.endMinute ?? null,
            weekdays: [...rule.time.weekdays]
          }
        }
      : {}),
    ...(rule.attachment
      ? {
          attachment: {
            minimumCount: rule.attachment.minimumCount ?? 1,
            maximumCount: rule.attachment.maximumCount ?? null,
            name: rule.attachment.name ? cloneText(rule.attachment.name) : null,
            mimeTypes: [...(rule.attachment.mimeTypes ?? [])],
            extensions: [...(rule.attachment.extensions ?? [])],
            minimumBytes: rule.attachment.minimumBytes ?? null,
            maximumBytes: rule.attachment.maximumBytes ?? null
          }
        }
      : {}),
    ...(rule.prompt ? { prompt: { prompt: rule.prompt.prompt } } : {})
  };
};

export const emailConfigFromResponse = (
  source: TriggerEmailConfigFieldsFragment
): EmailConfigDraft => ({
  schemaVersion: source.schemaVersion,
  arrivals: source.arrivals,
  privacy: source.privacy,
  timezone: source.timezone ?? null,
  scope: {
    mode: source.scope.mode,
    accounts: source.scope.accounts.map((account) => ({
      accountId: account.accountId,
      folders: {
        ids: [...account.folders.ids],
        allFolders: account.folders.allFolders,
        includeJunkAndDeleted: account.folders.includeJunkAndDeleted
      }
    })),
    folders: source.scope.folders
      ? {
          standard: [...source.scope.folders.standard],
          paths: [...source.scope.folders.paths],
          allFolders: source.scope.folders.allFolders,
          includeJunkAndDeleted: source.scope.folders.includeJunkAndDeleted
        }
      : null
  },
  rule: source.rule ? emailRuleInput(source.rule) : null
});

/** Error path for one selected mailbox; the editor reads errors with the same key. */
export const accountScopePath = (index: number) => `scope.accounts.${index}`;

export const validateEmailConfig = (config: TriggerEmailConfigInput): EmailErrors => {
  const errors: EmailErrors = {};
  const add = (path: string, message: string) => {
    errors[path] ??= message;
  };
  const integer = (value: number, max = Number.MAX_SAFE_INTEGER) =>
    Number.isSafeInteger(value) && value >= 0 && value <= max;
  const values = (list: string[], path: string, required = true, message?: string) => {
    if ((required && list.length === 0) || list.length > EMAIL_LIMITS.values)
      add(path, message ?? (required ? 'Enter 1 to 32 values.' : 'Use no more than 32 values.'));
    if (
      list.some((value) => !value.trim() || bytes(value) > EMAIL_LIMITS.bytes) ||
      new Set(list).size !== list.length
    )
      add(path, 'Values must be filled in, different from each other and under 2,000 characters.');
  };
  const text = (match: TriggerEmailTextMatchInput, path: string) => {
    if (!validEnum(match.operator, TextOperator))
      add(`${path}.operator`, 'Choose a supported text operator.');
    if (!validEnum(match.valueMode ?? ValueMode.Any, ValueMode))
      add(`${path}.valueMode`, 'Choose any value or all values.');
    values(match.values, `${path}.values`);
    // RE2 syntax is validated by the backend, not by JavaScript's different regex engine.
  };
  const address = (match: TriggerEmailAddressMatchInput, path: string) => {
    if (!validEnum(match.kind, AddressKind))
      add(`${path}.kind`, 'Choose an address, domain, user or profile.');
    if (!validEnum(match.valueMode ?? ValueMode.Any, ValueMode))
      add(`${path}.valueMode`, 'Choose any value or all values.');
    values(match.values, `${path}.values`);
    if (match.includeSubdomains && match.kind !== AddressKind.Domain)
      add(`${path}.includeSubdomains`, 'Subdomains apply only to domains.');
    if (match.kind === AddressKind.Address && match.values.some((value) => !value.includes('@')))
      add(`${path}.values`, 'Use complete email addresses.');
    if (
      match.kind === AddressKind.Domain &&
      match.values.some(
        (value) =>
          bytes(value) > 253 ||
          !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*$/i.test(value)
      )
    )
      add(`${path}.values`, 'Use domains without @ or wildcards.');
  };
  if (config.schemaVersion !== 2) add('schemaVersion', 'Unsupported email configuration version.');
  if ((config.arrivals ?? Arrival.New) !== Arrival.New)
    add('arrivals', 'Only new email arrivals are supported.');
  if (!validEnum(config.privacy ?? Privacy.MatchedMailboxOwners, Privacy))
    add('privacy', 'Choose a supported workspace privacy policy.');
  const scope = config.scope;
  if (scope.mode === ScopeMode.Selected) {
    const accounts = scope.accounts ?? [];
    if (!accounts.length || accounts.length > EMAIL_LIMITS.values || scope.folders)
      add('scope', 'Choose 1 to 32 mailboxes with their own folder selections.');
    const seen = new Set<string>();
    accounts.forEach((account, index) => {
      const path = accountScopePath(index);
      if (
        !account.accountId.trim() ||
        bytes(account.accountId) > 256 ||
        seen.has(account.accountId)
      )
        add(`${path}.accountId`, 'Use unique nonempty account IDs.');
      seen.add(account.accountId);
      if (account.folders.allFolders) {
        if (account.folders.ids?.length)
          add(`${path}.folders`, 'All folders cannot contain explicit IDs.');
      } else {
        if (account.folders.includeJunkAndDeleted)
          add(`${path}.folders`, 'Junk/Deleted inclusion applies only to All folders.');
        values(
          account.folders.ids ?? [],
          `${path}.folders.ids`,
          true,
          'Choose at least one folder (up to 32), or All folders.'
        );
      }
    });
  } else if (scope.mode === ScopeMode.All) {
    const folders = scope.folders;
    if (scope.accounts?.length || !folders)
      add('scope', 'All mailboxes use one role/path selection, not account IDs.');
    if (folders) {
      const roles = folders.standard ?? [],
        paths = folders.paths ?? [];
      if (folders.allFolders) {
        if (roles.length || paths.length)
          add('scope.folders', 'All folders cannot contain roles or paths.');
      } else if (
        folders.includeJunkAndDeleted ||
        roles.length + paths.length < 1 ||
        roles.length + paths.length > EMAIL_LIMITS.values
      ) {
        add('scope.folders', 'Choose 1 to 32 roles/full paths or explicit All folders.');
      }
      if (roles.some((role) => !isEmailFolderRole(role)) || new Set(roles).size !== roles.length)
        add('scope.folders.standard', 'Choose unique supported non-draft folder roles.');
      const folded = paths.map((path) => path.toLowerCase());
      if (
        new Set(folded).size !== folded.length ||
        paths.some(
          (path) =>
            path !== path.trim() ||
            !path ||
            bytes(path) > 2048 ||
            path.split('/').some((part) => !part || part === '.' || part === '..')
        )
      )
        add(
          'scope.folders.paths',
          'Use unique case-insensitive full paths without empty or relative segments.'
        );
    }
  } else add('scope.mode', 'Choose selected or all eligible mailboxes.');
  if (config.timezone !== null && config.timezone !== undefined) {
    try {
      if (!config.timezone || config.timezone === 'Local') throw new Error('Invalid time zone');
      new Intl.DateTimeFormat('en', { timeZone: config.timezone });
    } catch {
      add('timezone', 'Choose a valid time zone.');
    }
  }
  const seen = new Set<string>();
  let nodes = 0,
    prompts = 0;
  const visit = (rule: TriggerEmailRuleInput, path: string, depth: number) => {
    if (++nodes > EMAIL_LIMITS.nodes || depth > EMAIL_LIMITS.depth) {
      add(path, 'Condition tree exceeds its depth or node limit.');
      return;
    }
    if (!rule.id.trim() || bytes(rule.id) > 128 || seen.has(rule.id))
      add(`${path}.id`, 'Use unique nonempty condition IDs (maximum 128 bytes).');
    seen.add(rule.id);
    if (!validEnum(rule.kind, Kind)) add(`${path}.kind`, 'Choose a supported condition kind.');
    const children = rule.children ?? [];
    const payloads = [
      rule.phrase,
      rule.sender,
      rule.recipient,
      rule.time,
      rule.attachment,
      rule.prompt
    ].filter(Boolean).length;
    if (isEmailGroup(rule.kind)) {
      if (payloads || !children.length || (rule.kind === Kind.Not && children.length !== 1))
        add(path, 'AND/OR need children only; NOT needs exactly one child.');
      children.forEach((child, i) => visit(child, `${path}.children.${i}`, depth + 1));
      return;
    }
    if (payloads !== 1 || children.length)
      add(path, 'A condition needs only its own settings and no children.');
    switch (rule.kind) {
      case Kind.Phrase:
        if (!rule.phrase || !validEnum(rule.phrase.field, PhraseField))
          add(`${path}.phrase`, 'Choose subject, body or either.');
        if (rule.phrase) text(rule.phrase.match, `${path}.phrase.match`);
        break;
      case Kind.Sender:
        if (!rule.sender) add(`${path}.sender`, 'Sender settings are required.');
        if (rule.sender) address(rule.sender, `${path}.sender`);
        break;
      case Kind.Recipient:
        if (!rule.recipient || !validEnum(rule.recipient.field, RecipientField))
          add(`${path}.recipient`, 'Choose To, Cc or both.');
        if (rule.recipient) address(rule.recipient.match, `${path}.recipient.match`);
        break;
      case Kind.Time: {
        const time = rule.time;
        if (!time || !validEnum(time.operator, TimeOperator))
          add(`${path}.time`, 'Choose a supported time operator.');
        if (!config.timezone) add('timezone', 'Choose a time zone for time conditions.');
        if (!time) break;
        if (!integer(time.minute, 1439) || !time.weekdays.length)
          add(`${path}.time`, 'Choose a time and at least one weekday.');
        if (
          time.weekdays.some((day) => !EMAIL_WEEKDAYS.includes(day)) ||
          new Set(time.weekdays).size !== time.weekdays.length
        )
          add(`${path}.time.weekdays`, 'Choose valid days without repeats.');
        if (time.operator === TimeOperator.Between) {
          if (
            time.endMinute == null ||
            !integer(time.endMinute, 1440) ||
            time.endMinute === time.minute
          )
            add(
              `${path}.time.endMinute`,
              'Choose a different end time, including 24:00 if needed.'
            );
        } else if (time.endMinute != null)
          add(`${path}.time.endMinute`, 'Only Between uses an end time.');
        break;
      }
      case Kind.Attachment: {
        const attachment = rule.attachment;
        if (!attachment) {
          add(`${path}.attachment`, 'Attachment settings are required.');
          break;
        }
        const minimum = attachment.minimumCount ?? 1;
        if (
          !integer(minimum, 2147483647) ||
          (attachment.maximumCount != null &&
            (!integer(attachment.maximumCount, 2147483647) || attachment.maximumCount < minimum))
        )
          add(`${path}.attachment`, 'Attachment count bounds are invalid.');
        const min = attachment.minimumBytes,
          max = attachment.maximumBytes;
        if (
          (min != null && !integer(min)) ||
          (max != null && !integer(max)) ||
          (min != null && max != null && max < min)
        )
          add(
            `${path}.attachment.bytes`,
            'Use nonnegative safe-integer byte bounds in ascending order.'
          );
        if (attachment.name) text(attachment.name, `${path}.attachment.name`);
        const mimes = attachment.mimeTypes ?? [],
          extensions = attachment.extensions ?? [];
        values(mimes, `${path}.attachment.mimeTypes`, false);
        values(extensions, `${path}.attachment.extensions`, false);
        if (
          mimes.some((mime) => !/^[a-z0-9!#$%&'*+.^_`|~-]+\/[a-z0-9!#$%&'*+.^_`|~-]+$/i.test(mime))
        )
          add(
            `${path}.attachment.mimeTypes`,
            'Use file types like application/pdf, without spaces or extra parameters.'
          );
        if (extensions.some((extension) => !/^[a-z0-9]+$/.test(extension)))
          add(`${path}.attachment.extensions`, 'Use lowercase extensions without a dot.');
        break;
      }
      case Kind.Prompt:
        if (!rule.prompt?.prompt.trim() || bytes(rule.prompt.prompt) > EMAIL_LIMITS.bytes)
          add(`${path}.prompt`, 'Write an instruction of up to 2,000 characters.');
        if (++prompts > EMAIL_LIMITS.prompts)
          add(path, 'At most three prompt conditions are allowed.');
        break;
    }
  };
  if (config.rule) visit(config.rule, 'rule', 1);
  return errors;
};

export const validateEmailTarget = (target: TriggerTargetInput): EmailErrors => {
  const errors: EmailErrors = {};
  if (target.type !== TriggerTargetType.WorkspaceRun)
    errors.target = 'Choose the supported workspace-run target.';
  if (target.presetId != null && !target.presetId.trim())
    errors.target = 'Choose a nonempty custom preset ID.';
  if (target.presetId && target.preset)
    errors.target = 'Choose a custom or built-in preset, not both.';
  if (target.useCase && !target.preset) errors.target = 'A use case requires its built-in preset.';
  if (!target.presetId && !target.preset && !target.prompt?.trim())
    errors.target = 'Choose a preset or write instructions.';
  return errors;
};

export const validateEmailAvailability = (
  config: TriggerEmailConfigInput,
  target: TriggerTargetInput,
  options: EmailOptions | null | undefined,
  folders: Readonly<Record<string, EmailFolders | undefined>> = {}
): EmailErrors => {
  if (!options)
    return {
      options:
        'Mailbox settings are not ready. Loading or permission errors must be resolved before saving.'
    };
  const errors = validateEmailTarget(target);
  if (
    (config.privacy ?? Privacy.MatchedMailboxOwners) === Privacy.MatchedMailboxOwners &&
    !options.privacySupported
  )
    errors.privacy =
      'Matched-mailbox-owner privacy is unavailable. Choose a supported policy explicitly; privacy will not be downgraded.';
  (config.scope.accounts ?? []).forEach((account, i) => {
    if (
      !options.accounts.some(
        (option) =>
          option.id === account.accountId && option.status === MailboxAccountStatus.Connected
      )
    )
      errors[accountScopePath(i)] = 'Select an eligible connected Outlook mailbox.';
    const catalog = folders[account.accountId];
    if (!catalog)
      errors[`${accountScopePath(i)}.folders`] =
        'Folder settings are not ready. Resolve loading or catalogue errors.';
    else if (
      (account.folders.ids ?? []).some(
        (id) =>
          !catalog.some((folder) => folder.id === id && folder.role?.toUpperCase() !== 'DRAFTS')
      )
    )
      errors[`${accountScopePath(i)}.folders.ids`] =
        'Select non-draft folders belonging to this mailbox.';
  });
  const visit = (rule: TriggerEmailRuleInput) => {
    for (const match of [rule.sender, rule.recipient?.match]) {
      if (!match) continue;
      if (
        match.kind === AddressKind.User &&
        match.values.some(
          (id) =>
            !options.users.some((user) => user.id === id && user.state === AgencyUserState.Active)
        )
      )
        errors[`rule.${rule.id}.values`] = 'Select active users in this agency.';
      if (
        match.kind === AddressKind.AccessProfile &&
        match.values.some((id) => !options.profiles.some((profile) => profile.id === id))
      )
        errors[`rule.${rule.id}.values`] = 'Select access profiles in this agency.';
    }
    (rule.children ?? []).forEach(visit);
  };
  if (config.rule) visit(config.rule);
  if (
    target.presetId &&
    !options.presets.some((preset) => preset.id === target.presetId && preset.ready)
  )
    errors.target = 'Choose an available, ready custom preset.';
  if (
    target.preset &&
    !options.builtInPresets.some(
      (preset) =>
        preset.preset === target.preset &&
        (!target.useCase || preset.useCases.includes(target.useCase))
    )
  )
    errors.target = 'Choose an allowed built-in preset and its use case.';
  return errors;
};

export const buildEmailCreateInput = (
  name: string,
  config: TriggerEmailConfigInput,
  target: TriggerTargetInput,
  visibility?: VisibilityConfigInput
): CreateTriggerDefinitionInput => {
  if (!name.trim()) throw new Error('Trigger name is required.');
  if (Object.keys(validateEmailConfig(config)).length)
    throw new Error('Resolve email configuration errors before saving.');
  if (Object.keys(validateEmailTarget(target)).length)
    throw new Error('Resolve workspace-run target errors before saving.');
  return {
    name: name.trim(),
    eventMatcher: {
      eventType: TriggerEventType.EmailReceived,
      emailConfig: {
        schemaVersion: config.schemaVersion,
        scope: {
          mode: config.scope.mode,
          accounts: (config.scope.accounts ?? []).map((account) => ({
            accountId: account.accountId,
            folders: {
              ids: [...(account.folders.ids ?? [])],
              allFolders: account.folders.allFolders ?? false,
              includeJunkAndDeleted: account.folders.includeJunkAndDeleted ?? false
            }
          })),
          folders: config.scope.folders
            ? {
                standard: [...(config.scope.folders.standard ?? [])],
                paths: [...(config.scope.folders.paths ?? [])],
                allFolders: config.scope.folders.allFolders ?? false,
                includeJunkAndDeleted: config.scope.folders.includeJunkAndDeleted ?? false
              }
            : null
        },
        arrivals: config.arrivals ?? Arrival.New,
        privacy: config.privacy ?? Privacy.MatchedMailboxOwners,
        timezone: config.timezone ?? null,
        rule: config.rule ? emailRuleInput(config.rule) : null
      }
    },
    target: {
      type: target.type,
      presetId: target.presetId ?? null,
      preset: target.preset ?? null,
      useCase: target.useCase ?? null,
      prompt: target.prompt ?? null
    },
    ...(visibility
      ? {
          visibility: {
            level: visibility.level,
            userIds: visibility.userIds ? [...visibility.userIds] : null,
            teamIds: visibility.teamIds ? [...visibility.teamIds] : null
          }
        }
      : {})
  };
};

export const isEmailVersionConflict = (message: string) =>
  /trigger definition version -?\d+ does not match current version -?\d+/.test(message);
